import React, { useState, useMemo, useRef } from 'react';
import { UserData } from '../types';
import { CloseIcon } from './Icons';
import { stickerTextStyle } from './StickerText';

interface LeaderboardsProps {
    usersData: Record<string, UserData>;
    currentUserId: string | null;
    onNavigateToProfile: (id: string) => void;
    onHoldProfile?: (id: string) => void;
}

export const Leaderboards = ({ usersData, currentUserId, onNavigateToProfile, onHoldProfile }: LeaderboardsProps) => {
    const [tab, setTab] = useState<'global' | 'connections'>('global');
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const longPressFiredRef = useRef<boolean>(false);

    const usersArray = useMemo(() => Object.keys(usersData).filter(id => id !== 'system').map(id => ({ id, ...usersData[id] } as (UserData & { id: string }))), [usersData]);

    const globalList = useMemo(() => {
        return [...usersArray].sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0)).slice(0, 100);
    }, [usersArray]);

    const connectionsList = useMemo(() => {
        const cur = usersData[currentUserId || ''];
        if (!cur) return [] as (UserData & { id: string })[];
        const set = new Set<string>();
        // Include friends (symmetrical) and followers/following (asymmetrical)
        (cur.friends || []).forEach(u => set.add(u));
        (cur.following || []).forEach(u => set.add(u));
        (cur.followers || []).forEach(u => set.add(u));
        const list = usersArray.filter(u => set.has(u.id));
        return list.sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));
    }, [usersArray, usersData, currentUserId]);

    const startLongPress = (id: string) => {
        longPressFiredRef.current = false;
        if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = setTimeout(() => {
            longPressFiredRef.current = true;
            onHoldProfile?.(id);
        }, 400);
    };

    const cancelLongPress = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const renderRow = (user: UserData & { id: string }, idx: number) => (
        <div
            key={user.id}
            className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-800/40 cursor-pointer select-none"
            onClick={() => {
                if (longPressFiredRef.current) {
                    longPressFiredRef.current = false;
                    return;
                }
                onNavigateToProfile(user.id);
            }}
            onMouseDown={() => startLongPress(user.id)}
            onMouseUp={cancelLongPress}
            onMouseLeave={cancelLongPress}
            onMouseEnter={() => {
                if (window.matchMedia('(pointer: fine)').matches) {
                    onHoldProfile?.(user.id);
                }
            }}
            onTouchStart={() => startLongPress(user.id)}
            onTouchEnd={cancelLongPress}
        >
            <div className="w-10 text-sm font-bold text-gray-400" style={stickerTextStyle(0.18)}>#{idx + 1}</div>
            <img src={user.pfp} alt={user.id} className="w-10 h-10 rounded-full object-cover" />
            <div className="flex-1">
                <div className="font-semibold text-sm" style={stickerTextStyle(0.18)}>{user.displayName || user.id}</div>
                <div className="text-xs font-bold text-gray-400" style={stickerTextStyle(0.18)}>& {user.followerNickname || 'Followers'}</div>
            </div>
            <div className="text-sm font-bold text-blue-400" style={stickerTextStyle(0.18)}>{user.totalPoints || 0} pts</div>
        </div>
    );

    return (
        <div className="">
            <h2 className="text-2xl font-bold mb-4" style={stickerTextStyle(0.18)}>Leaderboards</h2>
            <div className="flex gap-2 mb-4">
                <button onClick={() => setTab('global')} className={`py-2 px-3 font-bold rounded-full ${tab === 'global' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-200'}`} style={stickerTextStyle(0.18)}>Global</button>
                <button onClick={() => setTab('connections')} className={`py-2 px-3 font-bold rounded-full ${tab === 'connections' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-200'}`} style={stickerTextStyle(0.18)}>{usersData[currentUserId ?? '']?.followerNickname || 'Followers'}</button>
            </div>

            <div className="mb-8 p-6 rounded-2xl space-y-4" style={{ backgroundColor: 'var(--app-theme-bg)' }}>
                {tab === 'global' && (
                    <div className="space-y-1">
                        {globalList.map((u, i) => renderRow(u, i))}
                    </div>
                )}
                {tab === 'connections' && (
                    <div className="space-y-1">
                        {connectionsList.length === 0 ? <div className="p-4 text-gray-400">No connections to show.</div> : connectionsList.map((u, i) => renderRow(u, i))}
                    </div>
                )}
            </div>
        </div>
    );
};

export const LeaderboardModal = ({ isOpen, onClose, usersData, onNavigateToProfile, onHoldProfile }: { isOpen: boolean, onClose: () => void, usersData: Record<string, UserData>, onNavigateToProfile: (userId: string) => void, onHoldProfile?: (userId: string) => void }) => {
    const leaderboard = useMemo(() => {
        return Object.entries(usersData)
            .filter(([username]) => username !== 'system')
            .map(([username, data]) => ({ username, ...data }))
            .sort((a, b) => {
                if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
                return 0; // return b.streak - a.streak;
            });
    }, [usersData]);

    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const longPressFiredRef = useRef<boolean>(false);

    const handleNavigate = (userId: string) => {
        onNavigateToProfile(userId);
        onClose();
    };

    const startLongPress = (id: string) => {
        longPressFiredRef.current = false;
        if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = setTimeout(() => {
            longPressFiredRef.current = true;
            onHoldProfile?.(id);
        }, 400);
    };

    const cancelLongPress = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80]" onClick={onClose}>
            <div className="rounded-[2.5rem] shadow-lg p-6 max-w-xl w-full mx-4 h-[70vh] flex flex-col backdrop-blur-md border border-white/10" style={{ backgroundColor: 'var(--app-theme-bg)' }} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h2 className="text-xl font-bold" style={stickerTextStyle(0.18)}>Leaderboard</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10"><CloseIcon className="w-5 h-5" /></button>
                </div>
                <div className="flex-grow overflow-y-auto -mx-6 px-6">
                    <ul className="space-y-1">
                        {leaderboard.map((user, index) => (
                            <li key={user.username}>
                                <button
                                    onClick={() => {
                                        if (longPressFiredRef.current) {
                                            longPressFiredRef.current = false;
                                            return;
                                        }
                                        handleNavigate(user.username);
                                    }}
                                    onMouseDown={() => startLongPress(user.username)}
                                    onMouseUp={cancelLongPress}
                                    onMouseLeave={cancelLongPress}
                                    onMouseEnter={() => {
                                        if (window.matchMedia('(pointer: fine)').matches) {
                                            onHoldProfile?.(user.username);
                                        }
                                    }}
                                    onTouchStart={() => startLongPress(user.username)}
                                    onTouchEnd={cancelLongPress}
                                    className={`w-full text-left flex items-center gap-4 p-2 rounded-lg hover:bg-white/10 transition-colors ${index < 3 ? 'bg-yellow-500/10' : ''}`}
                                >
                                    <span className={`w-8 text-center text-lg font-bold ${index < 3 ? 'text-yellow-400' : 'text-gray-400'}`} style={stickerTextStyle(0.18)}>{index + 1}</span>
                                    <img src={user.pfp} alt={user.username} className="w-12 h-12 rounded-full object-cover" />
                                    <div className="flex-grow">
                                        <span className="font-bold" style={stickerTextStyle(0.18)}>{user.displayName}</span>
                                        {/* <p className="text-sm font-bold text-gray-400" style={stickerTextStyle(0.18)}>Latest streak: {user.streak} 🔥</p> */}
                                    </div>
                                    <span className="font-bold text-yellow-400" style={stickerTextStyle(0.18)}>{user.totalPoints} pts</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};
